import React, { useEffect, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { useTheme } from "@mui/material/styles";

import useDataSourceManager from "hooks/useDataSourceManager";

import GlobalAlert from "components/Notification";
import ModalContainer from "components/ModalContainer";
import Loading from "../loading";

import Api from "../util/api";
import { defaultUserAccountObj } from "../util/const";
import Form from "../util/form";
import { toJSON } from "../util/func";
import MainView from "./MainView";

const getViewStateTitle = (selectedUserAccountProfile) => {
  return selectedUserAccountProfile ? "Edit User Account Profile" : "Create User Account Profile";
};

const ProfileUserAccountModal = ({
  auth,
  uuidList,
  openModal,
  selectedUserAccountProfile,
  showEditGeneral = true,
  handleCloseModal,
  onSaveChanges,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);

  const [loading, setLoading] = useState(true);
  const [formValue, setFormValue] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const pendingRequestRef = useRef({
    create: false,
    update: false,
  });

  const init = () => {
    setLoading(true);

    if (selectedUserAccountProfile) {
      const { id, name, userAccountList } = selectedUserAccountProfile.ref;

      setFormValue({
        id,
        name,
        userAccounts: cloneDeep(userAccountList || []),
      });
    } else {
      setFormValue(cloneDeep(defaultUserAccountObj));
    }

    setLoading(false);
  };

  const closeModal = () => {
    setFormValue(cloneDeep(defaultUserAccountObj));
    setLoading(true);
    handleCloseModal();
  };

  const getViewTitle = () => {
    return getViewStateTitle(selectedUserAccountProfile);
  };

  function HandleMainView() {
    return {
      changeFormValue: (key, value) => {
        setFormValue((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
      addUserAccounts: (newUserAccounts) => {
        setFormValue((prev) => ({
          ...prev,
          userAccounts: [...prev.userAccounts, ...newUserAccounts],
        }));
      },
      importUserAccounts: (replaceFlag, newUserAccounts) => {
        setFormValue((prev) => {
          const finalUserAccounts = [...prev.userAccounts];

          if (replaceFlag) {
            newUserAccounts.forEach((newItem) => {
              const username = newItem.username?.trim();
              const matchedIndexes = finalUserAccounts.reduce((acc, existingItem, index) => {
                if (existingItem.username?.trim() === username) {
                  acc.push(index);
                }
                return acc;
              }, []);

              if (matchedIndexes.length > 0) {
                for (let i = matchedIndexes.length - 1; i >= 0; i--) {
                  finalUserAccounts.splice(matchedIndexes[i], 1);
                }
              }

              finalUserAccounts.push(newItem);
            });
          } else {
            finalUserAccounts.push(...newUserAccounts);
          }

          return {
            ...prev,
            userAccounts: finalUserAccounts,
          };
        });
      },
      changeUserAccountItemValue: (index, key, value) => {
        setFormValue((prev) => {
          const newUserAccounts = [...prev.userAccounts];
          newUserAccounts[index] = {
            ...newUserAccounts[index],
            [key]: value,
          };
          return {
            ...prev,
            userAccounts: newUserAccounts,
          };
        });
      },
      deleteSelectedUserAccounts: (selectedIndexes) => {
        setFormValue((prev) => {
          const newUserAccounts = prev.userAccounts.filter((_, index) => !selectedIndexes.includes(index));
          return {
            ...prev,
            userAccounts: newUserAccounts,
          };
        });
      },
      checkFormValid: () => {
        if (!formValue.name || formValue.name.trim() === "") {
          return false;
        }

        if (formValue.userAccounts.length === 0) {
          return false;
        }

        return formValue.userAccounts.every((item) =>
          item.username?.trim() && item.password?.trim()
        );
      },
      handleSave: () => {
        setLoading(true);

        const finalFormValue = {
          name: formValue.name,
          userAccounts: formValue.userAccounts,
        };

        if (selectedUserAccountProfile) {
          const { id } = selectedUserAccountProfile.ref;
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, id);
          const payload = toJSON(finalFormValue, id);

          pendingRequestRef.current.update = true;
          sendRequest(apiKey, uuidList.update, params, payload);
        } else {
          const payload = toJSON(finalFormValue);
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.create);

          pendingRequestRef.current.create = true;
          sendRequest(apiKey, uuidList.create, params, payload);
        }
      },
    };
  }

  useEffect(() => {
    if (openModal) {
      init();
    } else {
      setFormValue(cloneDeep(defaultUserAccountObj));
      setLoading(true);
    }
  }, [openModal]);

  useEffect(() => {
    const createResponse = getData(uuidList.create);

    if (createResponse && pendingRequestRef.current.create) {
      pendingRequestRef.current.create = false;

      if (createResponse.hasOwnProperty("code") && createResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: createResponse?.message,
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap?.[uuidList.create]]);

  useEffect(() => {
    const updateResponse = getData(uuidList.update);

    if (updateResponse && pendingRequestRef.current.update) {
      pendingRequestRef.current.update = false;

      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message,
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap?.[uuidList.update]]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="auto">
        {loading && <Loading modalModel />}
        {formValue && (
          <MainView
            getViewTitle={getViewTitle}
            form={form}
            formValue={formValue}
            closeModal={closeModal}
            handleMainView={HandleMainView()}
            isEditMode={!!selectedUserAccountProfile}
            showEditGeneral={showEditGeneral}
          />
        )}
      </ModalContainer>

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ProfileUserAccountModal;
